import { entitySync, type EntityType, type IEntityChangeEvent } from "./entity-sync";

/**
 * Universal cached summary of an entity used across UI card tiers,
 * chips, and link resolution helpers.
 */
export interface ICachedEntitySummary {
  id: string;
  type: EntityType;
  name: string;
  displayName?: string;
  title?: string;
  photoUrl?: string;
  photoIconUrl?: string;
  thumbnailUrl?: string;
  isOwner?: boolean;
  isFiveStar?: boolean;
  updatedAt?: number;
}

/**
 * Cached query list entry supporting Stale-While-Revalidate (SWR) semantics.
 */
export interface IQueryListCacheEntry<T = any> {
  items: T[];
  updatedAt: number;
  expiresAt: number;
  stale: boolean;
}

/**
 * Options for `getOrFetchList` SWR query execution.
 */
export interface IGetOrFetchListOptions<T = any> {
  queryKey?: string;
  ttlMs?: number;
  forceRefresh?: boolean;
  onRevalidate?: (freshItems: T[]) => void;
}

const DEFAULT_LIST_CACHE_TTL_MS = 30_000;

/**
 * In-memory client-side cache and resolution registry for all core domain entities
 * (contacts, groups, media, memories, shares, workflows).
 *
 * Keeps UI chips and cards populated with human-readable labels and avatars
 * even when parent screens load entities in isolation or without full joins,
 * and provides a Stale-While-Revalidate (SWR) list cache synchronized with real-time
 * WebSocket `entitySync` broadcasts.
 */
class EntityCacheService {
  private cache = new Map<string, ICachedEntitySummary>();
  private listCache = new Map<string, IQueryListCacheEntry<any>>();
  private inflightListFetches = new Map<string, Promise<any[]>>();

  constructor() {
    // Automatically keep cache fresh when real-time mutation broadcasts arrive
    entitySync.subscribe((event: IEntityChangeEvent) => {
      this.handleSyncEvent(event);
    });
  }

  private buildKey(type: EntityType, id: string): string {
    return `${type}:${id}`;
  }

  private buildListKey(type: EntityType, queryKey = "default"): string {
    return `${type}::${queryKey}`;
  }

  /**
   * Registers a single entity into the in-memory cache.
   * Extracts canonical display names, titles, and image thumbnails.
   */
  public registerEntity(type: EntityType, raw: any): void {
    if (!raw || typeof raw !== "object") return;
    const id = raw.id || raw.contactId || raw.groupId || raw.mediaId || raw.memoryId;
    if (!id || typeof id !== "string" || id === "unknown") return;

    const resolvedName =
      raw.displayName ||
      raw.name ||
      raw.title ||
      raw.filename ||
      raw.label ||
      "";

    if (!resolvedName && this.cache.has(this.buildKey(type, id))) {
      return;
    }

    const photoUrl = raw.photoIconUrl || raw.photoUrl || raw.thumbnailUrl || raw.downloadUrl || raw.url;
    const isOwner = typeof raw.isOwner === "boolean" ? raw.isOwner : raw.userRole === "owner";
    const isFiveStar = raw.favouriteRating === 5 || raw.isFiveStar === true;

    const summary: ICachedEntitySummary = {
      id,
      type,
      name: resolvedName,
      displayName: resolvedName,
      title: resolvedName,
      photoUrl,
      photoIconUrl: photoUrl,
      thumbnailUrl: photoUrl,
      isOwner,
      isFiveStar,
      updatedAt: Date.now(),
    };

    this.cache.set(this.buildKey(type, id), summary);
  }

  /**
   * Batch-registers an array of entities into the cache.
   */
  public registerEntities(type: EntityType, items: any[]): void {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      this.registerEntity(type, item);
      // Also register any nested hydrated items
      if (item && typeof item === "object") {
        if (Array.isArray(item.contactItems)) {
          this.registerEntities("contact", item.contactItems);
        }
        if (Array.isArray(item.groupItems)) {
          this.registerEntities("group", item.groupItems);
        }
        if (Array.isArray(item.mediaItems)) {
          this.registerEntities("media", item.mediaItems);
        }
        if (Array.isArray(item.memoryItems)) {
          this.registerEntities("memory", item.memoryItems);
        }
      }
    }
  }

  /**
   * Alias for registerEntity.
   */
  public set(type: EntityType, raw: any): void {
    this.registerEntity(type, raw);
  }

  /**
   * Alias for registerEntities.
   */
  public setMany(type: EntityType, items: any[]): void {
    this.registerEntities(type, items);
  }

  /**
   * Retrieves a cached entity summary by type and ID.
   */
  public getCachedEntity(type: EntityType, id?: string): ICachedEntitySummary | undefined {
    if (!id || typeof id !== "string") return undefined;
    return this.cache.get(this.buildKey(type, id));
  }

  /**
   * Retrieves a cached contact by ID.
   */
  public getCachedContact(id?: string): { id: string; displayName: string; photoIconUrl?: string; isOwner?: boolean } | undefined {
    const summary = this.getCachedEntity("contact", id);
    if (!summary || !summary.displayName) return undefined;
    return {
      id: summary.id,
      displayName: summary.displayName,
      photoIconUrl: summary.photoIconUrl,
      isOwner: summary.isOwner,
    };
  }

  /**
   * Retrieves a cached group by ID.
   */
  public getCachedGroup(id?: string): { id: string; name: string; photoIconUrl?: string } | undefined {
    const summary = this.getCachedEntity("group", id);
    if (!summary || !summary.name) return undefined;
    return {
      id: summary.id,
      name: summary.name,
      photoIconUrl: summary.photoIconUrl,
    };
  }

  /**
   * Retrieves a cached media item by ID.
   */
  public getCachedMedia(id?: string): { id: string; title: string; thumbnailUrl?: string; downloadUrl?: string; isOwner?: boolean; isFiveStar?: boolean } | undefined {
    const summary = this.getCachedEntity("media", id);
    if (!summary || !summary.title) return undefined;
    return {
      id: summary.id,
      title: summary.title,
      thumbnailUrl: summary.thumbnailUrl,
      downloadUrl: summary.thumbnailUrl,
      isOwner: summary.isOwner,
      isFiveStar: summary.isFiveStar,
    };
  }

  /**
   * Retrieves a cached memory by ID.
   */
  public getCachedMemory(id?: string): { id: string; title: string; isFiveStar?: boolean } | undefined {
    const summary = this.getCachedEntity("memory", id);
    if (!summary || !summary.title) return undefined;
    return {
      id: summary.id,
      title: summary.title,
      isFiveStar: summary.isFiveStar,
    };
  }

  /**
   * Stores a list of entities in the SWR query cache and registers all item summaries.
   *
   * @template T - Entity model type.
   * @param {EntityType} type - The entity domain type.
   * @param {T[]} items - The list of entities to cache.
   * @param {string} [queryKey="default"] - Optional query discriminator key.
   * @param {number} [ttlMs=30000] - Freshness TTL in milliseconds.
   */
  public setCachedList<T = any>(
    type: EntityType,
    items: T[],
    queryKey = "default",
    ttlMs = DEFAULT_LIST_CACHE_TTL_MS
  ): void {
    if (!Array.isArray(items)) return;
    this.registerEntities(type, items);
    const now = Date.now();
    this.listCache.set(this.buildListKey(type, queryKey), {
      items: [...items],
      updatedAt: now,
      expiresAt: now + ttlMs,
      stale: false,
    });
  }

  /**
   * Retrieves a cached entity list and its freshness state.
   *
   * @template T - Entity model type.
   * @param {EntityType} type - The entity domain type.
   * @param {string} [queryKey="default"] - Optional query discriminator key.
   * @returns {{ items: T[]; isFresh: boolean; isStale: boolean } | undefined} Cached list metadata or undefined.
   */
  public getCachedList<T = any>(
    type: EntityType,
    queryKey = "default"
  ): { items: T[]; isFresh: boolean; isStale: boolean } | undefined {
    const entry = this.listCache.get(this.buildListKey(type, queryKey));
    if (!entry) return undefined;
    const isFresh = !entry.stale && entry.expiresAt > Date.now();
    return {
      items: [...entry.items],
      isFresh,
      isStale: !isFresh,
    };
  }

  /**
   * Invalidates cached entity lists for a specific type (and optionally a specific queryKey),
   * or all cached lists when `type` is omitted.
   *
   * @param {EntityType} [type] - Optional entity type to invalidate.
   * @param {string} [queryKey] - Optional specific query key to invalidate.
   */
  public invalidateListCache(type?: EntityType, queryKey?: string): void {
    if (!type) {
      this.listCache.clear();
      return;
    }
    if (queryKey) {
      const entry = this.listCache.get(this.buildListKey(type, queryKey));
      if (entry) {
        entry.stale = true;
        entry.expiresAt = 0;
      }
      return;
    }
    const prefix = `${type}::`;
    this.listCache.forEach((entry, key) => {
      if (key.startsWith(prefix)) {
        entry.stale = true;
        entry.expiresAt = 0;
      }
    });
  }

  /**
   * Executes a Stale-While-Revalidate (SWR) list fetch:
   * - If a fresh cached list exists and `forceRefresh` is false, returns it immediately without network I/O.
   * - If a stale cached list exists and `onRevalidate` is provided, returns the cached list immediately
   *   and triggers a background fetch that invokes `onRevalidate(freshItems)` upon completion.
   * - Otherwise awaits the network fetcher (deduplicating concurrent in-flight requests for the same key),
   *   caches the result, and returns it.
   *
   * @template T - Entity model type.
   * @param {EntityType} type - The entity domain type.
   * @param {() => Promise<T[]>} fetcher - Async function that fetches the fresh entity list.
   * @param {IGetOrFetchListOptions<T>} [options] - SWR cache options.
   * @returns {Promise<T[]>} Resolved list of entities.
   */
  public async getOrFetchList<T = any>(
    type: EntityType,
    fetcher: () => Promise<T[]>,
    options: IGetOrFetchListOptions<T> = {}
  ): Promise<T[]> {
    const {
      queryKey = "default",
      ttlMs = DEFAULT_LIST_CACHE_TTL_MS,
      forceRefresh = false,
      onRevalidate,
    } = options;
    const listKey = this.buildListKey(type, queryKey);

    if (!forceRefresh) {
      const cached = this.getCachedList<T>(type, queryKey);
      if (cached) {
        if (cached.isFresh) {
          return cached.items;
        }
        if (onRevalidate) {
          void this.runDeduplicatedFetch(listKey, type, queryKey, ttlMs, fetcher)
            .then((fresh) => {
              onRevalidate(fresh);
            })
            .catch(() => {
              // Background revalidation errors are non-fatal when serving stale cache
            });
          return cached.items;
        }
      }
    }

    return this.runDeduplicatedFetch(listKey, type, queryKey, ttlMs, fetcher);
  }

  private async runDeduplicatedFetch<T = any>(
    listKey: string,
    type: EntityType,
    queryKey: string,
    ttlMs: number,
    fetcher: () => Promise<T[]>
  ): Promise<T[]> {
    const existing = this.inflightListFetches.get(listKey);
    if (existing) {
      return (await existing) as T[];
    }

    const promise = (async (): Promise<T[]> => {
      try {
        const items = await fetcher();
        if (Array.isArray(items)) {
          this.setCachedList(type, items, queryKey, ttlMs);
        }
        return items;
      } finally {
        this.inflightListFetches.delete(listKey);
      }
    })();

    this.inflightListFetches.set(listKey, promise);
    return promise;
  }

  /**
   * Clears all cached entity entries and query lists.
   */
  public clear(): void {
    this.cache.clear();
    this.listCache.clear();
    this.inflightListFetches.clear();
  }

  private handleSyncEvent(event: IEntityChangeEvent): void {
    if (!event.type) return;

    if (event.type === "share") {
      // Share changes can affect visibility/roles across all shared domain lists
      this.invalidateListCache("memory");
      this.invalidateListCache("media");
      this.invalidateListCache("group");
      this.invalidateListCache("contact");
      this.invalidateListCache("share");
      return;
    }

    const prefix = `${event.type}::`;

    if (event.action === "delete" && event.id) {
      this.cache.delete(this.buildKey(event.type, event.id));
      this.listCache.forEach((entry, key) => {
        if (key.startsWith(prefix)) {
          entry.items = entry.items.filter((item: any) => item?.id !== event.id);
          entry.stale = true;
          entry.expiresAt = 0;
        }
      });
      return;
    }

    if (event.id && event.data) {
      this.registerEntity(event.type, { ...event.data, id: event.id });
    }

    if (event.action === "update" && event.id && event.data) {
      this.listCache.forEach((entry, key) => {
        if (key.startsWith(prefix)) {
          entry.items = entry.items.map((item: any) =>
            item?.id === event.id ? { ...item, ...event.data, id: event.id } : item
          );
          entry.stale = true;
          entry.expiresAt = 0;
        }
      });
      return;
    }

    this.invalidateListCache(event.type);
  }
}

export const entityCache = new EntityCacheService();
